import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Check, Users, Award, Target, Eye, Heart, ArrowRight, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';

const About = () => {
  const values = [
    { icon: Target, title: 'Excellence', description: 'We strive for excellence in every service we provide, ensuring quality outcomes.' },
    { icon: Heart, title: 'Integrity', description: 'Honest and transparent dealings form the foundation of our client relationships.' },
    { icon: Users, title: 'Client-Centric', description: 'Your success is our priority. We tailor solutions to meet your specific needs.' },
    { icon: Eye, title: 'Innovation', description: 'We embrace technology to deliver faster and more efficient services.' },
  ];

  const milestones = [
    { year: '2019', title: 'Founded', description: 'Started with a vision to simplify legal compliance for businesses.' },
    { year: '2020', title: '500+ Clients', description: 'Reached our first major milestone serving businesses across India.' },
    { year: '2022', title: 'Expanded Services', description: 'Added comprehensive GST and tax filing services to our portfolio.' },
    { year: '2024', title: '2500+ Clients', description: 'Trusted by thousands of businesses for their legal needs.' },
  ];

  const team = [
    { name: 'GK', role: 'Founder & Legal Expert', image: 'G' },
    { name: 'Expert Team', role: 'CA & Legal Professionals', image: 'E' },
    { name: 'Support Team', role: 'Client Relations', image: 'S' },
  ];

  const whyChooseUs = [
    'Expert team of CAs and Legal professionals',
    'Transparent pricing with no hidden charges',
    'Quick turnaround time for all services',
    'Dedicated support throughout the process',
    'Pan-India service coverage',
    '100% compliance guarantee',
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="about-page">
      <Helmet>
        <title>About Us - GK Legal Solutions | Trusted CA & Legal Firm</title>
        <meta name="description" content="Learn about GK Legal Solutions - 6+ years of experience, 2500+ happy clients. Expert CA and legal professionals for company registration, GST, and compliance services." />
        <link rel="canonical" href="https://gklegalsolutions.com/about" />
      </Helmet>
      {/* Hero Section */}
      <section className="section-spacing bg-gradient-to-br from-slate-50 to-blue-50/50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="font-body text-sm font-semibold text-primary uppercase tracking-wider">About Us</span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mt-3 mb-6">
                Your Trusted Partner for Business Success
              </h1>
              <p className="font-body text-lg text-slate-600 leading-relaxed mb-6">
                GK Legal Solutions is a leading business registration and compliance firm dedicated to helping entrepreneurs and businesses navigate the complex world of legal and tax requirements.
              </p>
              <p className="font-body text-base text-slate-600 leading-relaxed mb-8">
                With over 6 years of experience and a team of expert professionals, we have successfully assisted more than 2500 businesses in starting and growing their ventures across India.
              </p>
              <Link to="/contact">
                <Button className="btn-primary flex items-center gap-2" data-testid="about-contact-btn">
                  Get in Touch
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1560250097-0b93528c311a?w=800"
                alt="Professional office"
                className="rounded-2xl shadow-2xl w-full h-[450px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-primary text-white p-6 rounded-xl">
                <p className="font-heading text-4xl font-bold">6+</p>
                <p className="font-body text-sm">Years of Excellence</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-spacing bg-white">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-slate-50 p-10 rounded-2xl">
              <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-primary" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-4">Our Mission</h2>
              <p className="font-body text-slate-600 leading-relaxed">
                To simplify business registration and compliance for entrepreneurs across India, enabling them to focus on growing their businesses while we handle the legal complexities with expertise and efficiency.
              </p>
            </div>
            <div className="bg-slate-50 p-10 rounded-2xl">
              <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center mb-6">
                <Eye className="w-8 h-8 text-primary" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-4">Our Vision</h2>
              <p className="font-body text-slate-600 leading-relaxed">
                To become the most trusted name in business compliance services, known for our commitment to excellence, transparency, and customer satisfaction across the nation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="section-spacing bg-slate-50">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="font-body text-sm font-semibold text-primary uppercase tracking-wider">Our Values</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mt-3">
              What Drives Us
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white p-8 rounded-xl hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 bg-primary/10 rounded-lg flex items-center justify-center mb-6">
                  <value.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">{value.title}</h3>
                <p className="font-body text-sm text-slate-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-spacing bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="font-body text-sm font-semibold text-primary uppercase tracking-wider">Why Choose Us</span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mt-3 mb-8">
                The GK Legal Advantage
              </h2>
              <div className="space-y-4">
                {whyChooseUs.map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-green-600" />
                    </div>
                    <p className="font-body text-slate-700">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-primary text-white p-8 rounded-xl">
                <p className="font-heading text-5xl font-bold mb-2">2500+</p>
                <p className="font-body text-white/80">Happy Clients</p>
              </div>
              <div className="bg-slate-900 text-white p-8 rounded-xl">
                <p className="font-heading text-5xl font-bold mb-2">99.9%</p>
                <p className="font-body text-white/80">Success Rate</p>
              </div>
              <div className="bg-slate-900 text-white p-8 rounded-xl">
                <p className="font-heading text-5xl font-bold mb-2">6+</p>
                <p className="font-body text-white/80">Years Experience</p>
              </div>
              <div className="bg-primary text-white p-8 rounded-xl">
                <p className="font-heading text-5xl font-bold mb-2">10+</p>
                <p className="font-body text-white/80">Expert Team</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Milestones */}
      <section className="section-spacing bg-slate-50">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="font-body text-sm font-semibold text-primary uppercase tracking-wider">Our Journey</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mt-3">
              Milestones We've Achieved
            </h2>
          </div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-slate-200 transform -translate-x-1/2"></div>
            
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <div key={index} className={`flex flex-col md:flex-row items-center gap-8 ${index % 2 === 0 ? 'md:flex-row-reverse' : ''}`}>
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : ''}`}>
                    <div className="bg-white p-6 rounded-xl shadow-md inline-block">
                      <span className="font-heading text-3xl font-bold text-primary">{milestone.year}</span>
                      <h3 className="font-heading text-xl font-semibold text-slate-900 mt-2">{milestone.title}</h3>
                      <p className="font-body text-sm text-slate-600 mt-2">{milestone.description}</p>
                    </div>
                  </div>
                  <div className="w-4 h-4 bg-primary rounded-full relative z-10 hidden md:block"></div>
                  <div className="flex-1"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="section-spacing bg-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="font-body text-sm font-semibold text-primary uppercase tracking-wider">Our Team</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mt-3">
              Meet Our Experts
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {team.map((member, index) => (
              <div key={index} className="text-center group">
                <div className="w-32 h-32 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-primary transition-colors">
                  <span className="font-heading text-4xl font-bold text-primary group-hover:text-white transition-colors">
                    {member.image}
                  </span>
                </div>
                <h3 className="font-heading text-xl font-semibold text-slate-900">{member.name}</h3>
                <p className="font-body text-sm text-slate-600 mt-1">{member.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing bg-primary">
        <div className="container-custom text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Work With Us?
          </h2>
          <p className="font-body text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Let our experts handle your legal and compliance needs while you focus on growing your business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-white text-primary hover:bg-slate-100 px-8 py-3 font-semibold">
                Schedule a Consultation
              </Button>
            </Link>
            <a href="tel:+919820669943">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-3 font-semibold flex items-center gap-2">
                <Phone className="w-4 h-4" />
                +91 98206 69943
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
